import React, { useEffect } from 'react'
import PropTypes from 'prop-types'
import { compose } from 'recompose'
import { connect } from 'react-redux'
import { useHistory } from 'react-router-dom'
import StripeGiftPaymentForm from '../forms/StripeGiftPaymentForm'
import { selectors as memberSelectors } from 'member-reducer'
import { selectors } from '../reducer'
import * as apiActions from 'api-actions'
import { flashErrorMessage } from 'redux-flash'
import { Spinner } from 'lp-components'
import { PATH, makeGiftPath } from 'config'
import {
  API_ERROR_MAP,
  DigitalRecipientType,
  GiftContactType,
  ProductPrices,
} from 'types'
import {
  getGiftMembershipCost,
  handleSubmitFailWithFlashMessage,
  handleStripeGiftPaymentSubmit,
  usePaymentIntentManager,
} from 'utils'
import { StripeProvider } from 'components'

const propTypes = {
  billingContact: GiftContactType.isRequired,
  fetchStripePaymentIntent: PropTypes.func.isRequired,
  flashErrorMessage: PropTypes.func.isRequired,
  giftMessage: PropTypes.string,
  membershipPrices: ProductPrices,
  recipientContact: DigitalRecipientType.isRequired,
  selectedMembershipType: PropTypes.string.isRequired,
  sendSeaTowGift: PropTypes.func.isRequired,
  updateStripePaymentIntent: PropTypes.func.isRequired,
}

function StripeGiftPayment({
  billingContact,
  fetchStripePaymentIntent,
  flashErrorMessage,
  giftMessage = null,
  membershipPrices = null,
  recipientContact,
  selectedMembershipType,
  sendSeaTowGift,
  updateStripePaymentIntent,
}) {
  const history = useHistory()

  const totalCost = getGiftMembershipCost({
    membershipPrices,
    selectedMembershipType,
  })
  const giftAmount = totalCost > 0 ? totalCost : null

  const {
    paymentIntentId: stripePaymentIntentId,
    clientSecret,
    stripeCustomerId,
  } = usePaymentIntentManager({
    amount: giftAmount,
    fetchStripePaymentIntent,
    flashErrorMessage,
    updateStripePaymentIntent,
  })

  useEffect(() => {
    // A missing price means the catalog didn't load; point the user at a refresh.
    if (!totalCost || totalCost <= 0) {
      flashErrorMessage(API_ERROR_MAP.PRODUCT_PRICES_UNAVAILABLE)
    }
  }, [totalCost, flashErrorMessage])

  // Price couldn't be resolved (also flashed above). Show a message in place of
  // the payment form so the member knows why they can't continue.
  if (!giftAmount) {
    return (
      <p>
        <em>
          We're having trouble loading your order total. Please refresh the page
          to try again.
        </em>
      </p>
    )
  }

  if (!clientSecret) return <Spinner />

  const handleSubmit = (paymentMethodId) => {
    return handleStripeGiftPaymentSubmit({
      billingContact,
      giftMessage,
      membershipPrices,
      recipientContact,
      selectedMembershipType,
      sendSeaTowGift,
      stripeCustomerId,
      stripePaymentIntentId,
      stripePaymentMethodId: paymentMethodId,
    })
  }

  return (
    <StripeProvider>
      <StripeGiftPaymentForm
        onError={flashErrorMessage}
        onSubmit={handleSubmit}
        onSubmitSuccess={() => {
          history.push(makeGiftPath(PATH.CONFIRM_GIFT))
        }}
        onSubmitFail={handleSubmitFailWithFlashMessage}
        stripeClientSecretId={clientSecret}
      />
    </StripeProvider>
  )
}

StripeGiftPayment.propTypes = propTypes

function mapStateToProps(state) {
  return {
    billingContact: selectors.billingContact(state),
    giftMessage: selectors.giftMessage(state),
    membershipPrices: memberSelectors.membershipPrices(state),
    recipientContact: selectors.recipientContact(state),
    selectedMembershipType: selectors.selectedMembershipType(state),
  }
}

const mapDispatchToProps = {
  fetchStripePaymentIntent: apiActions.fetchStripePaymentIntent,
  flashErrorMessage,
  sendSeaTowGift: apiActions.sendSeaTowGift,
  updateStripePaymentIntent: apiActions.updateStripePaymentIntent,
}

export default compose(connect(mapStateToProps, mapDispatchToProps))(
  StripeGiftPayment,
)
